import React, { useState } from 'react';
import { fetchBusinessSuggestions } from '../services/api';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { ListBox } from 'primereact/listbox';
import { InputIcon } from 'primereact/inputicon';

// A prop típusának definiálása
interface GoogleBusinessSearchProps {
  onBusinessSelect: (business: Business) => void; // A kiválasztott vállalkozás átadása
}

interface Business {
  name: string;
  place_id: string;
  address: string;
}

const GoogleBusinessSearch: React.FC<GoogleBusinessSearchProps> = ({ onBusinessSelect }) => {
  const [query, setQuery] = useState<string>(''); // A keresett szöveg
  const [suggestions, setSuggestions] = useState<Business[]>([]); // Keresési javaslatok
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null); // Kiválasztott vállalkozás
  const [isLoading, setIsLoading] = useState<boolean>(false); // Betöltési állapot

  const fetchSuggestions = async (input: string) => {
    if (input.length <= 4) return;

    setIsLoading(true);
    try {
      const results = await fetchBusinessSuggestions(input);
      if (results) {
        setSuggestions(results);
      } else {
        console.error('Unexpected response format:', results);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setQuery(input);
    setSelectedBusiness(null);
    setSuggestions([]);

    if (input.length > 4) {
      fetchSuggestions(input);
    }
  };

  const handleSuggestionClick = (business: Business) => {
    setSelectedBusiness(business); // Kiválasztott vállalkozás mentése
  };

  const handleSubmit = () => {
    if (selectedBusiness) {
      // Az onBusinessSelect callback hívása a kiválasztott vállalkozás adataival
      onBusinessSelect(selectedBusiness);
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-center">Google Business Kereső</h1>

      <div className="p-field">
        <IconField iconPosition="left">
          {isLoading ? (
            <InputIcon className="pi pi-spinner pi-spin" />
          ) : (
            <InputIcon className="pi pi-search" />
          )}
          <InputText
            id="google-business-search"
            value={query}
            onChange={handleInputChange}
            placeholder="Kezdje el gépelni a vállalkozás nevét..."
            className="p-d-block w-full"
          />
        </IconField>
      </div>

      {isLoading && <ProgressSpinner style={{ display: 'block', margin: '2em auto' }} />}

      {!isLoading && suggestions.length > 0 && (
        <div className="p-grid p-dir-col">
          <ListBox
            value={selectedBusiness}
            options={suggestions}
            onChange={(e) => handleSuggestionClick(e.value)} // Listából való választás csak a kiválasztásra
            optionLabel="name"
            itemTemplate={(business: Business) => (
              <div className="p-3" style={{ borderBottom: '1px solid #ddd' }}>
                <div className="p-text-bold">{business.name}</div>
                <div>{business.address}</div>
              </div>
            )}
          />
        </div>
      )}

      {selectedBusiness && (
        <div className="selected-business">
          <h3>{selectedBusiness.name}</h3>
          <p><strong>Cím:</strong> {selectedBusiness.address}</p>
          <p><strong>Helyazonosító:</strong> {selectedBusiness.place_id}</p>

          <Button
            label="Kérés elküldése"
            icon="pi pi-check"
            onClick={handleSubmit} // Kérés elküldése gomb kattintáskor
            className="p-button-success"
          />
        </div>
      )}
    </div>
  );
};

export default GoogleBusinessSearch;
